import { useEffect, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import api from '../../services/api';

const HEADCOUNT_COLORS = ['#2563eb', '#94a3b8'];

export default function PayrollDashboard() {
  const [tab, setTab] = useState<'Analytics' | 'Reminders'>('Analytics');
  const [analytics, setAnalytics] = useState<any>(null);
  const [reminders, setReminders] = useState<any>(null);

  useEffect(() => {
    api.get('/payroll/dashboard/analytics').then((res) => setAnalytics(res.data));
    api.get('/payroll/dashboard/reminders').then((res) => setReminders(res.data));
  }, []);

  const headCountData = analytics
    ? [
        { name: 'Active', value: Number(analytics.headCount.active) },
        { name: 'Inactive', value: Number(analytics.headCount.inactive) },
      ]
    : [];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Payroll Dashboard</h1>
      <div className="flex gap-2 mb-6 border-b">
        {(['Analytics', 'Reminders'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium ${tab === t ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Analytics' && analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-gray-700 mb-2">Payroll Head Count</h3>
            {Number(analytics.headCount.active) + Number(analytics.headCount.inactive) > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={headCountData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={70} label>
                    {headCountData.map((d, i) => (
                      <Cell key={d.name} fill={HEADCOUNT_COLORS[i]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-400 py-8 text-center">No employees yet</p>
            )}
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-gray-700 mb-2">Payroll Comparison (Month over Month)</h3>
            {analytics.monthOverMonth.length === 0 ? (
              <p className="text-sm text-gray-400 py-8 text-center">No completed payroll runs yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={analytics.monthOverMonth}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(v: any) => `$${Number(v).toLocaleString()}`} />
                  <Line type="monotone" dataKey="total_net_pay" name="Net Pay" stroke="#2563eb" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-gray-700 mb-2">Employee Hires (last 6 months)</h3>
            {analytics.hireResignation.length === 0 ? (
              <p className="text-sm text-gray-400 py-8 text-center">No hires in the last 6 months</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={analytics.hireResignation}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="hires" fill="#16a34a" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-gray-700 mb-2">Payroll Initialization Details</h3>
            <ul className="text-sm divide-y">
              {analytics.initializationDetails.map((r: any) => (
                <li key={r.payroll_number} className="flex justify-between py-1">
                  <span>{r.payroll_number}</span>
                  <span>
                    {r.status} — {r.processed_employees}/{r.total_employees} ({r.error_count} errors)
                  </span>
                </li>
              ))}
              {analytics.initializationDetails.length === 0 && (
                <li className="py-4 text-gray-400 text-center">No payroll runs initialized yet</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {tab === 'Reminders' && reminders && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-gray-700 mb-2">EP / Work Permit Expiry</h3>
            <ul className="text-sm divide-y">
              {reminders.epWpExpiry.map((e: any) => (
                <li key={e.id} className="flex justify-between py-1">
                  <span>{e.code} — {e.first_name} {e.last_name}</span>
                  <span>{e.ep_wp_expiry_date}</span>
                </li>
              ))}
              {reminders.epWpExpiry.length === 0 && <li className="py-4 text-gray-400 text-center">None upcoming</li>}
            </ul>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-gray-700 mb-2">Custom Reminders</h3>
            <ul className="text-sm divide-y">
              {reminders.customReminders.map((r: any) => (
                <li key={r.id} className="flex justify-between py-1">
                  <span>{r.code} — {r.title}</span>
                  <span>{r.remind_date}</span>
                </li>
              ))}
              {reminders.customReminders.length === 0 && <li className="py-4 text-gray-400 text-center">None upcoming</li>}
            </ul>
          </div>
          <div className="bg-white rounded-lg shadow p-4 md:col-span-2">
            <h3 className="font-semibold text-gray-700 mb-2">Employment Type Breakdown</h3>
            {reminders.employmentTypeBreakdown.length === 0 ? (
              <p className="text-sm text-gray-400 py-8 text-center">No employees yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={reminders.employmentTypeBreakdown} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="employment_type" width={120} tickFormatter={(v) => v || 'Unspecified'} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#2563eb" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
